import React, {useState} from "react";
import {useForm} from "react-hook-form";
import {Trans, useTranslation} from "react-i18next";
import {CustomerFactory} from "../../../typescript-library";
import {getShopId} from "../../lib/getShopId";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import {RegistrationVoucher} from "../../../helpers/registrationVoucher";
import {useCustomerSelector} from "../../../store";
import {emailRegExp, mailCheckRun} from "../../../helpers/emailRegExp";
import {cdnDomain} from "../../../helpers/cnxDomains";

// url to image, without file extension!
const bannerPath = (device: string) =>
  `${cdnDomain[getShopId()]}/media/Newsletter/Banner-Newsletter-Anmeldung-${device}-Winter2024`;

const bannerDesktopPath = bannerPath("Desktop");
const bannerMobilePath = bannerPath("Mobil");

const NewsletterRegistrationBanner: React.FC = () => {
  const webp = {
    type: "image/webp",
    desktop: {
      fullHd: `${bannerDesktopPath}.webp`,
      // retina: `${bannerDesktopPath}@2x.webp`,
    },
    mobile: {
      fullHd: `${bannerMobilePath}.webp`,
      // retina: `${bannerMobilePath}@2x.webp`,
    },
  };
  const jpeg = {
    type: "image/jpg",
    desktop: {
      fullHd: `${bannerDesktopPath}.jpg`,
      // retina: `${bannerDesktopPath}@2x.jpg`,
    },
    mobile: {
      fullHd: `${bannerMobilePath}.jpg`,
      // retina: `${bannerMobilePath}@2x.jpg`,
    },
  };
  const {customer} = useCustomerSelector();
  const {t} = useTranslation();
  const [successState, setSuccessState] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: {errors},
    trigger,
  } = useForm<{email: string}>({
    mode: "onSubmit",
    defaultValues: {
      email: customer.email ?? "",
    },
  });

  const TransBlock: React.FC = () => {
    if (!RegistrationVoucher.enabled) {
      return t("newsletter.registrationDiscountShortText");
    }

    return (
      <Trans
        i18nKey="newsletter.registrationDiscount--amountFormatted"
        ns="translations"
        values={{
          amountFormatted: RegistrationVoucher.amountFormatted,
        }}
        components={{
          a: <sup />,
          b: <br />,
        }}
      />
    );
  };

  const onSubmit = handleSubmit((data: {email: string}) => {
    setIsLoading(true);
    void trigger();
    void CustomerFactory.createOrUpdateCustomer(getShopId(), {email: data.email, newsletterFlag: true}).then(() => {
      setCustomerEmail(data.email);
      setSuccessState(true);
      setIsLoading(false);
    });
  });

  const resendEmail = () => setSuccessState(false);

  const Image = () => (
    <div className="newsletter-banner__image">
      <picture>
        <source type={webp.type} srcSet={`${webp.desktop.fullHd}`} media="(min-width:768px)" />
        <source type={jpeg.type} srcSet={`${jpeg.desktop.fullHd}`} media="(min-width:768px)" />
        <source type={webp.type} srcSet={`${webp.mobile.fullHd}`} />
        <source type={jpeg.type} srcSet={`${jpeg.mobile.fullHd}`} />
        <img src={jpeg.mobile.fullHd} alt={t("newsletter.neverMissAnyDiscounts")} loading="lazy" />
      </picture>
    </div>
  );

  return (
    <div className="grid mx-0 mb-3 mb-l-9">
      <div className="newsletter-banner column-12">
        <Image />
        <div className="newsletter-banner__content overlay-box-vertical overlay-box-vertical--right dark-color-box py-m-9 px-m-7">
          <h3 className="uppercase regular mb-3">{t("newsletter.connoxNewsletter")}</h3>
          {successState && (
            <React.Fragment>
              <h3 className="uppercase medium">{t("newsletter.almostThere")}</h3>
              <p>
                <Trans
                  i18nKey="newsletter.callToConfirmNewsletter"
                  ns="translations"
                  values={{customerEmail}}
                  components={{
                    a: <strong />,
                  }}
                />
              </p>
              <p className="mb-0">
                <Trans
                  i18nKey="newsletter.resendMail"
                  ns="translations"
                  components={{
                    1: <button className="link" onClick={resendEmail} />,
                  }}
                />
              </p>
            </React.Fragment>
          )}

          {!successState && (
            <React.Fragment>
              <h2 className="uppercase medium">
                <TransBlock />
              </h2>
              <form onSubmit={(evt: React.FormEvent<HTMLFormElement>) => void onSubmit(evt)}>
                <span className="red">{errors.email && errors.email.message}</span>
                <div className="form-row">
                  <input
                    type="email"
                    className={errors.email ? "invalid" : ""}
                    placeholder={t("form.yourEmailAddress")}
                    {...register("email", {
                      required: true,
                      pattern: {
                        value: emailRegExp,
                        message: t("form.invalidEmailAddress"),
                      },
                      validate: (value: string) => mailCheckRun(value) || t("form.invalidEmailAddress"),
                    })}
                  />
                  <button className="cta-button cta-green" type="submit">
                    {isLoading ? <LoadingSpinner /> : <>{t("newsletter.subscribeNow")}</>}
                  </button>
                </div>
              </form>
            </React.Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterRegistrationBanner;
